/**
 * Onboarding completion persistence (APP-002 §6).
 *
 * DECISION: SAFE_LOCAL_MARKER.
 *
 * Discovery confirmed the backend exposes NO onboarding-completion field:
 *   - DashboardAccount / AccountResponse / OverviewResponse have no onboarding
 *     or profile-setup flag.
 *   - No account-profile endpoint exists beyond the rename endpoint.
 *   - DIRECT-004/009 feature docs describe no onboarding state.
 *
 * Per APP-002 §6 preferred order, with no backend field available we fall back
 * to a LOCAL non-authoritative completion marker. Constraints honored:
 *   - stores ONLY a boolean + version tag (no account data, Clerk IDs, tokens,
 *     plan, quota, or usage).
 *   - paired with successful backend account bootstrap (the gate reads the
 *     marker only AFTER AccountBootstrap resolves `ready` — APP-002 §6).
 *   - the user may revisit onboarding or product guidance later (a "replay"
 *     affordance clears the marker).
 *
 * Limitation documented in docs/APP-002: this is a UI convenience, not an
 * account-readiness authority. Backend account state always wins.
 */
const STORAGE_KEY = 'ei_onboarding_v1';

export interface OnboardingCompletionState {
  /** True when the local marker indicates onboarding was completed. */
  completed: boolean;
}

/**
 * Read the local onboarding completion marker. Returns `completed: false` for
 * any malformed, missing, or version-mismatched value. Never throws — storage
 * access can be denied (private mode, quota); in that case onboarding is
 * treated as incomplete (the user simply sees it again).
 */
export function readOnboardingCompletion(): OnboardingCompletionState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { completed: false };
    const parsed = JSON.parse(raw) as { completed?: unknown; v?: unknown };
    if (parsed.v !== 1) return { completed: false };
    return { completed: parsed.completed === true };
  } catch {
    return { completed: false };
  }
}

/**
 * Persist the local completion marker. Stores ONLY the boolean + version tag.
 * Failures (private mode, quota) are swallowed — completion is best-effort
 * UI state, never authoritative.
 */
export function writeOnboardingCompletion(completed: boolean): void {
  try {
    const payload = JSON.stringify({ v: 1, completed });
    window.localStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* Storage unavailable — onboarding remains a per-session experience. */
  }
}

/**
 * Clear the marker so the user can replay onboarding. Used by the account menu
 * / onboarding "replay" affordance (APP-002 §6: "allow the user to revisit
 * onboarding or product guidance later").
 */
export function clearOnboardingCompletion(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Storage unavailable — nothing to clear. */
  }
}
